import type { RenderComponentProps } from "replo-runtime/shared/types";

import React from "react";

import {
  RenderEnvironmentContext,
  useRuntimeContext,
} from "replo-runtime/shared/runtime-context";
import { ReploComponent } from "replo-runtime/store/components/ReploComponent";

import { useBundleBuilder } from "../BundleBuilderContext";

export const BundleGrid: React.FC<RenderComponentProps> = ({
  context,
  component,
  componentAttributes,
}) => {
  const { isPublishing } = useRuntimeContext(RenderEnvironmentContext);
  const { bundleTargetType, bundleProductsWithVariants, getProductData } =
    useBundleBuilder();
  const isGrid = component.props.layout === "grid";
  const tiles: { productId: string; variantId?: string }[] =
    React.useMemo(() => {
      if (!bundleProductsWithVariants) {
        return [];
      }

      return bundleProductsWithVariants.flatMap((product) => {
        const { productId, variantIds } = product;
        const productData = getProductData(productId);
        if (
          !productData ||
          (productData?.status !== "ACTIVE" && isPublishing)
        ) {
          return [];
        }
        if (bundleTargetType === "products") {
          return [{ productId }];
        }
        let variantIdsArray = variantIds ?? [];
        if (variantIdsArray.length === 0) {
          variantIdsArray =
            productData?.variants.map(({ id }) => String(id)) ?? [];
        }
        // If we're using variants, we need to check if a list is provided
        return variantIdsArray.map((variantId) => ({
          productId,
          variantId,
        }));
      });
    }, [
      bundleProductsWithVariants,
      bundleTargetType,
      getProductData,
      isPublishing,
    ]);
  const productTileComponent = component.children?.[0];
  return (
    <div
      {...componentAttributes}
      style={
        isGrid
          ? {
              display: "grid",
              gap: "24px",
              flexGrow: 1,
            }
          : {
              display: "flex",
              flexDirection: "column",
              maxHeight: "338px",
              gap: "7px",
              flex: "1 1 auto",
              minHeight: 0,
              overflowY: "auto",
            }
      }
    >
      {productTileComponent &&
        tiles?.map(({ productId, variantId }) => (
          <ReploComponent
            key={variantId ?? productId}
            context={context}
            component={{
              ...productTileComponent,
              props: { ...productTileComponent.props, productId, variantId },
            }}
          />
        ))}
    </div>
  );
};
